import type { ScanRunDetail } from '../components/ScanHistory';
import type { Finding, ScanResult } from '../types';
import type { SourceCount } from '../account';
import { hostOf } from './url';
import { setLabel } from './standing';

/**
 * "What do I change, and why" for one domain, from the runs in range.
 *
 * The report showed counts, charts and every question, then sent the reader
 * back to the tool for the plan ("Fix it is deliberately not printed"). A PDF
 * goes to people who cannot click, so a report without the to-do list was a
 * scoreboard. This turns the stored probes into a short, ordered list, each item
 * with the reason it is on the list.
 *
 * Built only from what was measured. Nothing here is a guess dressed as a
 * finding: a question nobody was named on gets no rewrite advice, because no
 * wording wins it (see ClaimYourPages), and a page is only named when an engine
 * actually cited it.
 */

interface Probe { query: string; state: string; competitors?: string[]; citations?: string[] }

export interface Asked {
  query: string;
  /** "SET on GPT", one per set × engine that lost this question. */
  where: string[];
  rivals: string[];
}

export interface PageFix {
  url: string;
  questions: Asked[];
  rivals: string[];
}

export interface ActionPlan {
  domain: string;
  /** 1. AI read this page, then named someone else. Edit the page. */
  pages: PageFix[];
  /**
   * 1b. Same loss, but the engine named only the site (Gemini reports a site
   * title, never a page), so the page to edit is the one that answers it.
   */
  siteOnly: Asked[];
  /** 2. How the site is built: readiness findings, worst first. */
  build: Finding[];
  /** 3. AI never reached you: off-site work, with the sources it used instead. */
  offSite: Asked[];
  targets: SourceCount[];
  /** Answered from your pages with no brand named at all: nothing to rewrite. */
  noBrand: number;
  /** Questions where AI named you, for the "keep doing this" line. */
  named: number;
}

const ENGINE = (e: string): string => (e === 'gemini' ? 'Gemini' : e === 'openai' ? 'GPT' : e);

function add(list: Map<string, Asked>, query: string, where: string, rivals: string[]): void {
  const a = list.get(query) ?? { query, where: [], rivals: [] };
  if (!a.where.includes(where)) a.where.push(where);
  a.rivals = [...new Set([...a.rivals, ...rivals])];
  list.set(query, a);
}

const SEVERITY = { critical: 0, warning: 1, info: 2 } as const;

/**
 * `details`: the latest run of each set × engine in range, newest first. One
 * plan per domain. `targets` come from the competitive view, which is already
 * filtered to the same range, and only make sense for a single domain.
 */
export function actionPlans(details: ScanRunDetail[], targets: SourceCount[] = []): ActionPlan[] {
  const byDomain = new Map<string, ScanRunDetail[]>();
  for (const d of details) byDomain.set(d.domain, [...(byDomain.get(d.domain) ?? []), d]);
  const single = byDomain.size === 1;

  return [...byDomain.entries()].map(([domain, runs]) => {
    const pages = new Map<string, { url: string; asked: Map<string, Asked> }>();
    const siteOnly = new Map<string, Asked>();
    const offSite = new Map<string, Asked>();
    let noBrand = 0;
    const namedQs = new Set<string>();

    for (const run of runs) {
      const where = `${setLabel(run)} on ${ENGINE(run.engine)}`;
      for (const p of (run.detail?.visibility ?? []) as Probe[]) {
        if (!p || typeof p.query !== 'string') continue;
        const rivals = p.competitors ?? [];
        if (p.state === 'mentioned') { namedQs.add(p.query); continue; }
        if (p.state === 'absent') { add(offSite, p.query, where, rivals); continue; }
        if (p.state !== 'cited_only') continue;
        if (rivals.length === 0) { noBrand++; continue; }
        const own = [...new Set((p.citations ?? []).filter((c) => {
          const h = hostOf(c);
          return /^https?:\/\//i.test(c) && (h === domain || h.endsWith(`.${domain}`));
        }))];
        if (own.length === 0) { add(siteOnly, p.query, where, rivals); continue; }
        for (const url of own) {
          const at = pages.get(url) ?? { url, asked: new Map<string, Asked>() };
          add(at.asked, p.query, where, rivals);
          pages.set(url, at);
        }
      }
    }

    // Readiness belongs to a page, so the newest readiness of this domain.
    const readiness = runs
      .map((r) => r.detail?.readiness as ScanResult | null)
      .find((r) => r && typeof r === 'object' && 'readiness' in r && Array.isArray(r.readiness?.findings));
    const build = (readiness?.readiness.findings ?? [])
      .filter((f) => f.severity !== 'info' && f.certainty !== 'unmeasured')
      .sort((a, b) => SEVERITY[a.severity] - SEVERITY[b.severity]);

    const pageList = [...pages.values()]
      .map((p) => {
        const questions = [...p.asked.values()];
        return { url: p.url, questions, rivals: [...new Set(questions.flatMap((q) => q.rivals))] };
      })
      .sort((a, b) => b.questions.length - a.questions.length);

    return {
      domain,
      pages: pageList,
      siteOnly: [...siteOnly.values()],
      build,
      // A question lost on one engine and won on another is not an off-site gap.
      offSite: [...offSite.values()].filter((a) => !namedQs.has(a.query)),
      targets: single ? targets.filter((t) => t.host !== domain && !t.host.endsWith(`.${domain}`)).slice(0, 10) : [],
      noBrand,
      named: namedQs.size,
    };
  });
}

/** Nothing to do: no page, no finding, no off-site gap. */
export const isEmptyPlan = (p: ActionPlan): boolean =>
  !p.pages.length && !p.siteOnly.length && !p.build.length && !p.offSite.length;
